import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { ValidationError } from "yup";

import { loadingAtom } from "@/states/loading/atoms";

import {
  ServiceInfo as ServiceInfoDto,
  CalendarInfo,
  ContentWebinarElement,
  EventSeminarElement,
  ReservationApiPutReservationCalendarOrderRequest,
  ReservationApiPutReservationOrderRequest,
  ServiceCancelSettings,
  ServiceReservationPeriodFlag,
  ServiceReservationSpecifiedPeriodFlag,
  ServiceReserveStatus,
  ServiceStatus,
  ServiceViewType,
  UserPlan,
  UserSpecialCommercialLaw,
  FormServiceElement,
  ServiceFormsType,
  DocumentRequestElement,
  SurveyElement,
  InquiryElement,
  EmailNewsLetterElement,
  ServiceScheduleChangeSettings,
  ServiceViewTermFlag,
  ServiceCustomFormsType,
  CapacityStatus,
  SubscriptionElement,
} from "@/apis/clients";

import { contentAccessLogApi } from "@/apis/content-access-log/contentAccessLogApi";
import { customerIndustryMasterApi } from "@/apis/master/customerIndustryMasterApi";
import { pageApi } from "@/apis/page/pageApi";
import reservationApi from "@/apis/reservation/reservationApi";
import serviceApi from "@/apis/service/serviceApi";
import { serviceSNSFormTypeToString } from "@/libs/enums/enumString";
import { serviceFormAtom } from "@/states/service-form/atoms";
import {
  usePutReservationOrderRequest,
  useServiceFormUiState,
} from "@/states/service-form/selectors";
import {
  getCustomerInputKeys,
  ServiceFormErrors,
  ServiceFormCustomerInputKey,
  ServiceFormServiceInputKey,
  ServiceFormServiceInputs,
  ServiceFormData,
  ServiceFormInputs,
  ServiceType,
  ServiceInfo,
  FormPayment,
  HoldDate,
  CardElementFormPayment,
  CustomFormInputKey,
  ServiceFormFieldsKey,
  ServiceFormFields,
  CustomFormInputs,
  SubscriptionServiceDetail,
} from "@/states/service-form/types";
import { assertNever, checkIsCustomField } from "@/utils/common";
import Yup from "@/utils/yup";
import { useRouter } from "next/router";
import { NEXT_URL } from "@/utils/const";

const CustomFieldText = "custom-field";

const CustomKeyRegex = RegExp(
  `${CustomFieldText}_([0-9]{1,})_([0-9]{1,})_([0-9]{1,})`,
);

const isEventSeminar = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is EventSeminarElement => {
  return (
    // serviceInfo.type === ServiceType.EVENT_SEMINAR_FREE ||
    serviceInfo.type === ServiceType.EVENT_SEMINAR
  );
};
const isContentWebinar = (
  serviceInfo: ServiceInfoDto | CalendarInfo,
): serviceInfo is ContentWebinarElement => {
  // return (
  //   serviceInfo.type === ServiceType.FREE_CONTENT_WEBINAR ||
  //   serviceInfo.type === ServiceType.CONTENT_SALES
  // );
  return false;
};
const isCalendar = (serviceInfo: ServiceInfoDto | CalendarInfo) => {
  return serviceInfo.type === ServiceType.CALENDAR_RESERVATION;
};
const isForm = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is FormServiceElement => {
  return serviceInfo.type === ServiceType.FORM;
};
const isSubscription = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is SubscriptionElement => {
  return serviceInfo.type === ServiceType.SUBSCRIPTION_SERVICE;
};
const isDocumentRequest = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is DocumentRequestElement => {
  return serviceInfo.type === ServiceType.DOCUMENT_REQUEST;
};
const isSurvey = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is SurveyElement => {
  return serviceInfo.type === ServiceType.SURVEY;
};
const isInQuiry = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is InquiryElement => {
  return serviceInfo.type === ServiceType.INQUIRY;
};
const isNewsLetter = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is EmailNewsLetterElement => {
  return serviceInfo.type === ServiceType.EMAIL_NEWSLETTER;
};
const isGeneralPurpose = (
  serviceInfo: ServiceInfoDto,
): serviceInfo is EventSeminarElement => {
  return serviceInfo.type === ServiceType.GENERAL_PURPOSE;
};

export const getCustomFieldKey = (
  serviceFormType: number,
  serviceFormId: number,
  serviceCustomFormId: number,
) => {
  if (serviceFormType === ServiceFormsType.UPLOAD) {
    return `${CustomFieldText}_upload`;
  }
  return `${CustomFieldText}_${serviceFormType}_${serviceFormId}_${serviceCustomFormId}`;
};

export const getIdsFormCustomKey = (key: string) => {
  if (CustomKeyRegex.test(key)) {
    const ids = key.split("_");
    return {
      formType: Number(ids[1]),
      serviceFormId: Number(ids[2]),
      serviceCustomFormId: Number(ids[3]),
    };
  }
  return null;
};

export const useFetchServiceFormByService = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    async (serviceId: number, pageId?: number) => {
      setServiceForm((prev) => {
        const formData: ServiceFormData = {
          service: {
            id: serviceId,
            type: "email-newsletter",
            viewType: ServiceViewType.FORM,
            name: "",
            options: [],
            specialCommercialLaw: UserSpecialCommercialLaw.INVALID,
            reservation_specified_period_flag:
              ServiceReservationSpecifiedPeriodFlag.UNSPECIFIED,
            viewTermFlag: ServiceViewTermFlag.HIDE,
          },
          holdDates: [],
          customer: [],
        };
        return {
          formId: undefined,
          pageId: pageId,
          isLoading: true,
          formData,
          inputs: prev.inputs,
          errors: {
            service: {},
            customer: {},
          } as ServiceFormErrors,
          options: {
            industries: [],
          },
          payment: {},
          paymentMethod: {},
          clientSecret: undefined,
          plan: UserPlan.FREE,
          draft: prev.draft,
          isAgreePolicy: prev.isAgreePolicy,
        };
      });

      try {
        const serviceRes = await serviceApi.get({ id: serviceId }, undefined);
        if (!serviceRes) return;

        // 業種マスタ取得
        const industriesRes =
          await customerIndustryMasterApi.getCustomerIndustries();
        const industries: { text: string; value: number }[] =
          industriesRes.data.map((industry) => ({
            text: industry.industry_name,
            value: industry.id,
          }));

        const holdDatesRes = await serviceApi.holdDates({ id: serviceId });

        const holdDates = holdDatesRes.data.map((holdDate) => ({
          id: holdDate.id,
          type: holdDate.type,
          serviceId: holdDate.service_id,
          start: holdDate.start,
          end: holdDate.end,
          price: holdDate.price,
          zip: holdDate.zip,
          prefMasterId: holdDate.pref_master_id,
          address1: holdDate.address1,
          address2: holdDate.address2,
          placeName: holdDate.place_name,
        }));

        setServiceForm((prev) => {
          const formData: ServiceFormData = {
            service: makeServiceInfo(serviceId, serviceRes.data),
            holdDates,
            customer: serviceRes.data.forms.map((form) => {
              const customFormTitle = serviceSNSFormTypeToString(
                form.form_type,
              );
              return {
                type: form.form_type,
                serviceFormId: form.service_form_id,
                customForm: {
                  serviceCustomFormId: form.custom_form.service_custom_form_id,
                  type: form.custom_form.custom_forms_type,
                  label:
                    customFormTitle === ""
                      ? form.custom_form.title || ""
                      : customFormTitle,
                  isRequired: !!form.custom_form.required_flag,
                  description: form.custom_form.custom_forms_description || "",
                  descriptionJson:
                    form.custom_form.custom_forms_description_json,
                  extraJson: form.custom_form.custom_forms_extra_json,
                  options: form.custom_form.custom_forms_options,
                  maximumFile: form.custom_form.custom_forms_maximum_file,
                  fileType: form.custom_form.custom_forms_file_type,
                },
              };
            }),
          };
          const hasMailMagazine = serviceRes.data.forms.some(
            (form) => form.form_type === ServiceFormsType.MAIL_DELIVERY,
          );
          const inputs: ServiceFormInputs = {
            ...prev.inputs,
            customer: {
              ...prev.inputs.customer,
              mailMagazine: hasMailMagazine || undefined,
            },
          };

          const errors = getErrors(inputs, formData);
          const payment = prev.payment;
          const paymentMethod = prev.paymentMethod;
          const clientSecret = prev.clientSecret;
          return {
            formId: `${serviceId}`,
            pageId: pageId,
            isLoading: false,
            formData,
            inputs,
            errors,
            options: {
              industries,
            },
            payment,
            paymentMethod,
            clientSecret,
            plan: serviceRes.data.plan,
            draft: prev.draft,
            isAgreePolicy: prev.isAgreePolicy,
          };
        });
      } catch (e) {
        setServiceForm((prev) => ({
          ...prev,
          isLoading: false,
        }));
        throw e;
      }
    },
    [setServiceForm],
  );
};

export const useFetchServiceForm = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    async (formId: string) => {
      setServiceForm((prev) => {
        const formData: ServiceFormData = {
          service: {
            id: 0,
            type: "content-webinar",
            viewType: ServiceViewType.BUTTON,
            name: "",
            specialCommercialLaw: UserSpecialCommercialLaw.INVALID,
            reservation_specified_period_flag:
              ServiceReservationSpecifiedPeriodFlag.UNSPECIFIED,
            options: [],
            viewTermFlag: ServiceViewTermFlag.HIDE,
          },
          holdDates: [],
          customer: [],
        };
        return {
          formId,
          pageId: undefined,
          isLoading: true,
          formData,
          inputs: prev.inputs,
          errors: {
            service: {},
            customer: {},
          } as ServiceFormErrors,
          options: {
            industries: [],
          },
          payment: {},
          paymentMethod: {},
          clientSecret: undefined,
          plan: UserPlan.FREE,
          draft: prev.draft,
          isAgreePolicy: prev.isAgreePolicy,
        };
      });

      try {
        const formRes = await pageApi.getForm({ uuid: formId }, undefined);
        const serviceId = formRes.service_id;
        if (!serviceId && serviceId !== 0) {
          return;
        }
        const serviceRes = await serviceApi.get({ id: serviceId }, undefined);

        // 業種マスタ取得
        const industriesRes =
          await customerIndustryMasterApi.getCustomerIndustries();
        const industries: { text: string; value: number }[] =
          industriesRes.data.map((industry) => ({
            text: industry.industry_name,
            value: industry.id,
          }));

        const holdDatesRes = await serviceApi.holdDates({ id: serviceId });

        const holdDates = holdDatesRes.data.map((holdDate) => ({
          id: holdDate.id,
          type: holdDate.type,
          serviceId: holdDate.service_id,
          start: holdDate.start,
          end: holdDate.end,
          price: holdDate.price,
          zip: holdDate.zip,
          prefMasterId: holdDate.pref_master_id,
          address1: holdDate.address1,
          address2: holdDate.address2,
          placeName: holdDate.place_name,
        }));

        setServiceForm((prev) => {
          const formData: ServiceFormData = {
            service: makeServiceInfo(serviceId, serviceRes.data),
            holdDates,
            customer: formRes.data.map((form) => {
              const customFormTitle = serviceSNSFormTypeToString(
                form.form_type,
              );
              return {
                type: form.form_type,
                serviceFormId: form.service_form_id,
                customForm: {
                  serviceCustomFormId: form.custom_form.service_custom_form_id,
                  type: form.custom_form.custom_forms_type,
                  label:
                    customFormTitle === ""
                      ? form.custom_form.title || ""
                      : customFormTitle,
                  isRequired: !!form.custom_form.required_flag,
                  description: form.custom_form.custom_forms_description || "",
                  descriptionJson:
                    form.custom_form.custom_forms_description_json,
                  extraJson: form.custom_form.custom_forms_extra_json,
                  options: form.custom_form.custom_forms_options,
                  maximumFile: form.custom_form.custom_forms_maximum_file,
                  fileType: form.custom_form.custom_forms_file_type,
                },
              };
            }),
          };
          const hasMailMagazine = formRes.data.some(
            (form) => form.form_type === ServiceFormsType.MAIL_DELIVERY,
          );
          const inputs: ServiceFormInputs = {
            ...prev.inputs,
            customer: {
              ...prev.inputs.customer,
              mailMagazine: hasMailMagazine || undefined,
            },
          };

          const errors = getErrors(inputs, formData);
          const payment = prev.payment;
          const paymentMethod = prev.paymentMethod;
          const clientSecret = prev.clientSecret;
          return {
            formId,
            pageId: formRes.page_id,
            isLoading: false,
            formData,
            inputs,
            errors,
            options: {
              industries,
            },
            payment,
            paymentMethod,
            clientSecret,
            plan: serviceRes.data.plan,
            draft: prev.draft,
            isAgreePolicy: prev.isAgreePolicy,
          };
        });
      } catch (e) {
        setServiceForm((prev) => ({
          ...prev,
          isLoading: false,
        }));
        throw e;
      }
    },
    [setServiceForm],
  );
};

export const useFetchServiceCalendarForm = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    async (formId: string) => {
      setServiceForm((prev) => {
        const formData: ServiceFormData = {
          service: {
            id: 0,
            supplierId: 0,
            status: ServiceStatus.PUBLISHING,
            type: "calendar",
            name: "",
            reservationPeriodFlag:
              ServiceReservationPeriodFlag.WITHOUT_RESERVATION,
            reservationSpecifiedPeriodFlag:
              ServiceReservationSpecifiedPeriodFlag.UNSPECIFIED,
            contactName: "",
            contactMail: "",
            cancelSettings: ServiceCancelSettings.INVALID,
            details: [],
            menus: [],
            specialCommercialLaw: UserSpecialCommercialLaw.INVALID,
            options: [],
            reserveStatus: ServiceReserveStatus.ALL,
            viewType: ServiceViewType.BUTTON,
            scheduleChangeSettings: ServiceScheduleChangeSettings.INVALID,
            reservation_specified_period_flag:
              ServiceReservationSpecifiedPeriodFlag.UNSPECIFIED,
            viewTermFlag: ServiceViewTermFlag.HIDE,
          },
          holdDates: [],
          customer: [],
        };
        const paymentMethod = prev.paymentMethod;
        return {
          formId,
          pageId: undefined,
          isLoading: true,
          formData,
          inputs: prev.inputs,
          errors: {
            service: {},
            customer: {},
          } as ServiceFormErrors,
          options: {
            industries: [],
          },
          payment: {},
          paymentMethod,
          clientSecret: undefined,
          plan: UserPlan.FREE,
          draft: prev.draft,
          isAgreePolicy: prev.isAgreePolicy,
        };
      });
      try {
        const formRes = await pageApi.getForm({ uuid: formId }, undefined);

        const serviceId = formRes.service_id;
        if (!serviceId && serviceId !== 0) {
          return;
        }
        const serviceRes = await serviceApi.getCalendarServiceById(
          { id: serviceId },
          undefined,
        );
        // 業種マスタ取得
        const industriesRes =
          await customerIndustryMasterApi.getCustomerIndustries();
        const industries: { text: string; value: number }[] =
          industriesRes.data.map((industry) => ({
            text: industry.industry_name,
            value: industry.id,
          }));

        const holdDates: HoldDate[] = [];

        setServiceForm((prev) => {
          const formData: ServiceFormData = {
            service: makeServiceCalendarInfo(serviceId, serviceRes.data),
            holdDates,
            customer: formRes.data.map((form) => {
              const customFormTitle = serviceSNSFormTypeToString(
                form.form_type,
              );
              return {
                type: form.form_type,
                serviceFormId: form.service_form_id,
                customForm: {
                  serviceCustomFormId: form.custom_form.service_custom_form_id,
                  type: form.custom_form.custom_forms_type,
                  label:
                    customFormTitle === ""
                      ? form.custom_form.title || ""
                      : customFormTitle,
                  isRequired: !!form.custom_form.required_flag,
                  description: form.custom_form.custom_forms_description || "",
                  descriptionJson:
                    form.custom_form.custom_forms_description_json,
                  extraJson: form.custom_form.custom_forms_extra_json,
                  options: form.custom_form.custom_forms_options,
                  maximumFile: form.custom_form.custom_forms_maximum_file,
                  fileType: form.custom_form.custom_forms_file_type,
                },
              };
            }),
          };

          let customFormFields = {};
          formRes.data.forEach((form) => {
            const item = {
              formType: form.form_type,
              serviceFormId: form.service_form_id,
              serviceCustomFormId: form.custom_form.service_custom_form_id,
              isRequired: !!form.custom_form.required_flag,
              selects: [],
              value: "",
            };
            const value =
              form.form_type === ServiceFormsType.SINGLE_LINE_DESCRIPTION ||
              form.form_type === ServiceFormsType.MULTI_LINE_DESCRIPTION
                ? ""
                : [];
            customFormFields = {
              ...customFormFields,
              [getCustomFieldKey(
                form.form_type,
                form.service_form_id,
                form.custom_form.service_custom_form_id,
              )]: value,
            };
          });
          const hasMailMagazine = formRes.data.some(
            (form) => form.form_type === ServiceFormsType.MAIL_DELIVERY,
          );
          const inputs = {
            ...prev.inputs,
            customFormFields,
            customer: {
              ...prev.inputs.customer,
              mailMagazine: hasMailMagazine || undefined,
            },
          };

          const errors = getErrors(inputs, formData);
          const payment = prev.payment;
          const paymentMethod = prev.paymentMethod;
          const clientSecret = prev.clientSecret;
          return {
            formId,
            isLoading: false,
            formData,
            inputs,
            errors,
            options: {
              industries,
            },
            payment,
            paymentMethod,
            pageId: formRes.page_id,
            clientSecret,
            plan: serviceRes.data.plan,
            draft: prev.draft,
            isAgreePolicy: prev.isAgreePolicy,
          };
        });
      } catch (e) {
        setServiceForm((prev) => ({
          ...prev,
          isLoading: false,
        }));
        throw e;
      }
    },
    [setServiceForm],
  );
};

const makeServiceInfo = (
  serviceId: number,
  serviceInfoDto: ServiceInfoDto,
): ServiceInfo => {
  if (isEventSeminar(serviceInfoDto) || isGeneralPurpose(serviceInfoDto)) {
    return {
      id: serviceId,
      supplierId: serviceInfoDto.supplier_id,
      type: "event-seminar",
      viewType: serviceInfoDto.view_type,
      name: serviceInfoDto.service_name,
      status: serviceInfoDto.status,
      contactName: serviceInfoDto.contact_name,
      contactMail: serviceInfoDto.contact_mail,
      contactTel: serviceInfoDto.contact_tel,
      reserveStatus: serviceInfoDto.reserve_status,
      reservationAcceptanceSetting:
        serviceInfoDto.reservation_acceptance_settings,
      cancelSettings: serviceInfoDto.cancel_settings,
      scheduleChangeSettings: serviceInfoDto.schedule_change_settings,
      disable_past_flag: !!serviceInfoDto.disable_past_flag,
      details: serviceInfoDto.details
        ? serviceInfoDto.details.map((detail) => ({
            id: Number(detail.id),
            eventStartAt: new Date(detail.event_start_time),
            start: new Date(detail.event_start_time),
            end: new Date(detail.event_end_time),
            eventEndAt: new Date(detail.event_end_time),
            placeName: detail.place_name || "",
            name: detail.name,
            price: detail.price,
            maximumNumber:
              detail.maximum_number !== null
                ? detail.maximum_number
                : undefined,
            remainingReservationCount: detail.remaining_reservation_count,
            type: detail.type,
            description: detail.describe,
            viewType: serviceInfoDto.view_type,
            maxTicketsPerOrder: detail.max_tickets_per_order,
            isShowFree: detail?.is_show_free,
          }))
        : [],
      options: serviceInfoDto.options
        ? serviceInfoDto.options.map((option) => ({
            id: Number(option.id),
            name: option.name,
            describe: option.describe,
            price: option.price,
            amount: option.price,
            time: 0,
            isShowFree: option?.is_show_free,
          }))
        : [],
      reservation_period_flag: serviceInfoDto.reservation_period_flag,
      reservation_specified_period_flag:
        serviceInfoDto.reservation_specified_period_flag,
      reservation_period_start_time:
        serviceInfoDto.reservation_period_start_time,
      reservation_period_end_time: serviceInfoDto.reservation_period_end_time,
      view_seats_left: serviceInfoDto.view_seats_left,
      paid_flag: serviceInfoDto.paid_flag,
      paymentmethod: serviceInfoDto.paymentmethod,
      cancel_price_settings: serviceInfoDto.cancel_price_settings,
      specialCommercialLaw: serviceInfoDto.special_commercial_law,
      viewTermFlag: serviceInfoDto.view_term_flag,
      gaTag: serviceInfoDto.ga_tag,
      gaCrossDomainTag: serviceInfoDto.ga_cross_domain_tag,
      gtmId: serviceInfoDto.gtm_id,
      maximum_capacity: serviceInfoDto.maximum_capacity,
      capacity_status: serviceInfoDto.capacity_status,
      remaining_capacity: serviceInfoDto.remaining_capacity,
    };
  }
  if (isForm(serviceInfoDto)) {
    return {
      id: serviceId,
      supplierId: serviceInfoDto.supplier_id,
      type: "form",
      viewType: serviceInfoDto.view_type,
      name: serviceInfoDto.service_name,
      status: serviceInfoDto.status,
      contactName: serviceInfoDto.contact_name,
      contactMail: serviceInfoDto.contact_mail,
      contactTel: serviceInfoDto.contact_tel,
      reserveStatus: serviceInfoDto.reserve_status,
      reservationAcceptanceSetting:
        serviceInfoDto.reservation_acceptance_settings,
      cancelSettings: serviceInfoDto.cancel_settings,
      scheduleChangeSettings: serviceInfoDto.schedule_change_settings,
      details: serviceInfoDto.details
        ? serviceInfoDto.details.map((detail) => ({
            id: Number(detail.id),
            eventStartAt: new Date(detail.event_start_time),
            start: new Date(detail.event_start_time),
            end: new Date(detail.event_end_time),
            eventEndAt: new Date(detail.event_end_time),
            placeName: detail.place_name || "",
            name: detail.name,
            price: detail.price,
            maximumNumber:
              detail.maximum_number !== null
                ? detail.maximum_number
                : undefined,
            remainingReservationCount: detail.remaining_reservation_count,
            type: detail.type,
            description: detail.describe,
            isShowFree: detail?.is_show_free,
          }))
        : [],
      options: serviceInfoDto.options
        ? serviceInfoDto.options.map((option) => ({
            id: Number(option.id),
            name: option.name,
            describe: option.describe,
            price: option.price,
            time: 0,
            isShowFree: option?.is_show_free,
          }))
        : [],
      reservation_period_flag: serviceInfoDto.reservation_period_flag,
      reservation_specified_period_flag:
        serviceInfoDto.reservation_specified_period_flag,
      reservation_period_start_time:
        serviceInfoDto.reservation_period_start_time,
      reservation_period_end_time: serviceInfoDto.reservation_period_end_time,
      view_seats_left: serviceInfoDto.view_seats_left,
      paid_flag: serviceInfoDto.paid_flag,
      paymentmethod: serviceInfoDto.paymentmethod,
      cancel_price_settings: serviceInfoDto.cancel_price_settings,
      viewTermFlag: serviceInfoDto.view_term_flag,
      gaTag: serviceInfoDto.ga_tag,
      gaCrossDomainTag: serviceInfoDto.ga_cross_domain_tag,
      gtmId: serviceInfoDto.gtm_id,
    };
  }
  if (isSubscription(serviceInfoDto)) {
    return {
      id: serviceId,
      type: "subscription",
      viewType: serviceInfoDto.view_type,
      name: serviceInfoDto.service_name,
      status: serviceInfoDto.status,
      contactName: serviceInfoDto.contact_name,
      contactMail: serviceInfoDto.contact_mail,
      contactTel: serviceInfoDto.contact_tel,
      reserveStatus: serviceInfoDto.reserve_status,
      details: serviceInfoDto.service_menus
        ? serviceInfoDto.service_menus.map(
            (menu) =>
              ({
                id: Number(menu.id),
                name: menu.menu_name,
                price: menu.price,
                isSetCapacity: !!menu.maximum_number_flag,
                capacity: menu.maximum_number,
                isSetContractPeriod: !!menu.contract_period_flag,
                contractPeriod: menu.contract_period_months,
                isSetFreeTrialPeriod: !!menu.trial_period_flag,
                freeTrialPeriod: menu.trial_period_days,
                description: menu.description,
                maximumNumber: menu.maximum_number,
                remainingReservationCount:
                  // NOTE: For subscriptions, the backend returns the *used* count inside 'remaining_reservation_count'.
                  // Therefore, we must subtract it from maximum_number to get the true remaining amount.
                  menu.maximum_number != null &&
                  menu.remaining_reservation_count != null
                    ? menu.maximum_number - menu.remaining_reservation_count
                    : undefined,
              } as SubscriptionServiceDetail),
          )
        : [],
      reservation_period_flag: serviceInfoDto.reservation_period_flag,
      reservation_specified_period_flag:
        serviceInfoDto.reservation_specified_period_flag,
      reservation_period_start_time:
        serviceInfoDto.reservation_period_start_time,
      reservation_period_end_time: serviceInfoDto.reservation_period_end_time,
      view_seats_left: serviceInfoDto.view_seats_left,
      paymentmethod: serviceInfoDto.paymentmethod,
      specialCommercialLaw: serviceInfoDto.special_commercial_law,
      viewTermFlag: serviceInfoDto.view_term_flag,
      maximum_capacity: serviceInfoDto.maximum_capacity,
      capacity_status: serviceInfoDto.capacity_status,
      remaining_capacity: serviceInfoDto.remaining_capacity,
      plan: serviceInfoDto.plan,
      gaTag: serviceInfoDto.ga_tag,
      gaCrossDomainTag: serviceInfoDto.ga_cross_domain_tag,
      gtmId: serviceInfoDto.gtm_id,
    };
  }
  if (isInQuiry(serviceInfoDto)) {
    return {
      id: serviceId,
      type: "inquiry",
      viewType: serviceInfoDto.view_type,
      name: serviceInfoDto.service_name,
      paid_flag: serviceInfoDto.paid_flag,
      options: [],
      reservation_specified_period_flag:
        serviceInfoDto.reservation_specified_period_flag,
      reservation_period_start_time:
        serviceInfoDto.reservation_period_start_time,
      reservation_period_end_time: serviceInfoDto.reservation_period_end_time,
      viewTermFlag: serviceInfoDto.view_term_flag,
      gaTag: serviceInfoDto.ga_tag,
      gaCrossDomainTag: serviceInfoDto.ga_cross_domain_tag,
      gtmId: serviceInfoDto.gtm_id,
    };
  }
  if (isCalendar(serviceInfoDto)) {
    return makeServiceCalendarInfo(serviceId, serviceInfoDto);
  }
  if (isSurvey(serviceInfoDto)) {
    const survey = serviceInfoDto as SurveyElement;
    return {
      id: serviceId,
      type: "survey",
      viewType: survey.view_type,
      name: survey.service_name,
      paid_flag: survey.paid_flag,
      options: [],
      reservation_specified_period_flag:
        survey.reservation_specified_period_flag,
      reservation_period_start_time: survey.reservation_period_start_time,
      reservation_period_end_time: survey.reservation_period_end_time,
      viewTermFlag: survey.view_term_flag,
      gaTag: survey.ga_tag,
      gaCrossDomainTag: survey.ga_cross_domain_tag,
      gtmId: survey.gtm_id,
    };
  }
  if (isDocumentRequest(serviceInfoDto)) {
    const document = serviceInfoDto as DocumentRequestElement;
    return {
      id: serviceId,
      type: "document-request",
      viewType: document.view_type,
      name: document.service_name,
      paid_flag: document.paid_flag,
      options: [],
      reservation_specified_period_flag:
        document.reservation_specified_period_flag,
      reservation_period_start_time: document.reservation_period_start_time,
      reservation_period_end_time: document.reservation_period_end_time,
      viewTermFlag: document.view_term_flag,
      gaTag: document.ga_tag,
      gaCrossDomainTag: document.ga_cross_domain_tag,
      gtmId: document.gtm_id,
    };
  }
  if (isNewsLetter(serviceInfoDto)) {
    const newsletter = serviceInfoDto as EmailNewsLetterElement;
    return {
      id: serviceId,
      type: "email-newsletter",
      viewType: newsletter.view_type,
      name: newsletter.service_name,
      paid_flag: newsletter.paid_flag,
      options: [],
      reservation_specified_period_flag:
        newsletter.reservation_specified_period_flag,
      reservation_period_start_time: newsletter.reservation_period_start_time,
      reservation_period_end_time: newsletter.reservation_period_end_time,
      viewTermFlag: newsletter.view_term_flag,
      gaTag: newsletter.ga_tag,
      gaCrossDomainTag: newsletter.ga_cross_domain_tag,
      gtmId: newsletter.gtm_id,
    };
  }

  return serviceInfoDto;
};

const makeServiceCalendarInfo = (
  serviceId: number,
  serviceInfoDto: CalendarInfo,
): ServiceInfo => {
  return {
    id: serviceId,
    supplierId: serviceInfoDto.supplier_id,
    type: "calendar",
    name: serviceInfoDto.service_name,
    status: serviceInfoDto.status,
    contactName: serviceInfoDto.contact_name,
    contactMail: serviceInfoDto.contact_mail,
    contactTel: serviceInfoDto.contact_tel,
    cancelSettings: serviceInfoDto.cancel_settings,
    scheduleChangeSettings: serviceInfoDto.schedule_change_settings,
    details:
      serviceInfoDto.details?.map((detail) => ({
        id: Number(detail.id),
        eventStartAt: detail.event_start_time
          ? new Date(detail.event_start_time)
          : undefined,
        eventEndAt: detail.event_end_time
          ? new Date(detail.event_end_time)
          : undefined,
        type: detail.type,
        url: detail.url,
        placeName: detail.place_name || "",
        zip: detail.zip,
        prefMasterId: detail.pref_master_id,
        address1: detail.address1,
        address2: detail.address2,
        maximumNumber:
          detail.maximum_number !== null ? detail.maximum_number : undefined,
        remainingReservationCount: 0,
        price: detail.price,
        viewType: ServiceViewType.BUTTON,
      })) ?? [],
    menus:
      serviceInfoDto.service_menus?.map((menu) => {
        return {
          id: menu.id,
          name: menu.menu_name,
          imageUrl: menu.menu_image_url,
          description: menu.description,
          timeRequired: menu.time_required,
          breakTime: menu.break_time,
          color: menu.color,
          price: menu.price ? menu.price : 0,
          displayOrder: menu.display_order,
          publicFlag: menu.public_flag,
          image: menu.menu_image_url,
          isShowTime: menu?.is_show_time,
          isShowFree: menu?.is_show_free,
          options:
            menu.options?.map((option) => ({
              id: option.id,
              name: option.name,
              amount: option.amount,
              time: option.time,
              describe: option.description,
              price: 0,
              isShowTime: option?.is_show_time,
              isShowFree: option?.is_show_free,
            })) ?? [],
        };
      }) ?? [],
    reservationPeriodFlag: serviceInfoDto.reservation_period_flag,
    reservationSpecifiedPeriodFlag:
      serviceInfoDto.reservation_specified_period_flag,
    reservationPeriodStartTime: serviceInfoDto.reservation_period_start_time,
    reservationPeriodEndTime: serviceInfoDto.reservation_period_end_time,
    paid_flag: serviceInfoDto.paid_flag,
    paymentmethod: serviceInfoDto.paymentmethod,
    cancel_price_settings: serviceInfoDto.cancel_price_settings,
    specialCommercialLaw: serviceInfoDto.special_commercial_law,
    options: [],
    reserveStatus: ServiceReserveStatus.SINGLE_SELECT,
    viewType: ServiceViewType.BUTTON,
    reservation_specified_period_flag:
      serviceInfoDto.reservation_specified_period_flag,
    reservation_period_start_time: serviceInfoDto.reservation_period_start_time,
    reservation_period_end_time: serviceInfoDto.reservation_period_end_time,
    viewTermFlag: serviceInfoDto.view_term_flag,
    gaTag: serviceInfoDto.ga_tag,
    gaCrossDomainTag: serviceInfoDto.ga_cross_domain_tag,
    gtmId: serviceInfoDto.gtm_id,
  };
};

export const useHandleFieldValueChange = <
  Key extends
    | ServiceFormFieldsKey
    | ServiceFormServiceInputKey
    | CustomFormInputKey,
>() => {
  const router = useRouter();
  const isConfirm =
    router.pathname === NEXT_URL.FORM_CONFIRM ||
    router.pathname === NEXT_URL.SUBSCRIPTION_PURCHASE_CONFIRM;
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    (
      fieldKey: Key,
      value: Key extends ServiceFormFieldsKey
        ? ServiceFormFields[Key] | undefined
        : Key extends ServiceFormServiceInputKey
        ? ServiceFormServiceInputs[Key] | undefined
        : CustomFormInputs[Key],
    ) => {
      setServiceForm((prev) => {
        // Trim emails: mobile keyboards append a space after a suggestion, which fails validation invisibly
        const normalizedValue =
          (fieldKey === "detailIds" ||
            fieldKey === "optionIds" ||
            fieldKey === "serviceMenuOptionIds") &&
          value === undefined
            ? []
            : (fieldKey === "email" || fieldKey === "subEmail") &&
              typeof value === "string"
            ? value.trim()
            : value;
        const inputs =
          fieldKey === "detailIds" ||
          fieldKey === "optionIds" ||
          fieldKey === "serviceMenuId" ||
          fieldKey === "serviceMenuOptionIds" ||
          fieldKey === "serviceMenuReservationDateTime" ||
          fieldKey === "quantity"
            ? {
                service: {
                  ...prev.inputs.service,
                  ...{
                    [fieldKey]: normalizedValue,
                  },
                },
                customer: prev.inputs.customer,
              }
            : {
                service: prev.inputs.service,
                customer: {
                  ...prev.inputs.customer,
                  ...{
                    [fieldKey]: normalizedValue,
                  },
                },
              };

        const errors = getErrors(inputs as ServiceFormInputs, prev.formData);

        const fieldUpdate = { ...prev };
        if (isConfirm) {
          const inputsDraft =
            fieldKey === "detailIds" ||
            fieldKey === "optionIds" ||
            fieldKey === "serviceMenuId" ||
            fieldKey === "serviceMenuOptionIds" ||
            fieldKey === "serviceMenuReservationDateTime" ||
            fieldKey === "quantity"
              ? {
                  service: {
                    ...prev?.draft.inputs.service,
                    ...{
                      [fieldKey]: normalizedValue,
                    },
                  },
                  customer: prev?.draft.inputs.customer,
                }
              : {
                  service: prev?.draft.inputs.service,
                  customer: {
                    ...prev?.draft.inputs.customer,
                    ...{
                      [fieldKey]: normalizedValue,
                    },
                  },
                };
          const errorsDraft = getErrors(
            inputsDraft as ServiceFormInputs,
            prev.formData,
          );

          fieldUpdate.draft = {
            ...fieldUpdate.draft,
            inputs: inputsDraft as ServiceFormInputs,
            errors: errorsDraft,
          };
        } else {
          fieldUpdate.inputs = inputs as ServiceFormInputs;
          fieldUpdate.errors = errors;
        }
        return fieldUpdate;
      });
    },
    [isConfirm, setServiceForm],
  );
};

// setData init formConfirm from common data
export const useInitDataConfirm = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(() => {
    setServiceForm((prev) => {
      return {
        ...prev,
        draft: { inputs: { ...prev.inputs }, errors: { ...prev.errors } },
      };
    });
  }, [setServiceForm]);
};

// merge data in formConfirm to common data
export const useSubmitDataConfirm = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(() => {
    setServiceForm((prev) => {
      if (prev.draft)
        return {
          ...prev,
          inputs: { ...prev.draft.inputs },
          errors: { ...prev.draft.errors },
        };
      return prev;
    });
  }, [setServiceForm]);
};

type ValidationSpec = {
  [Property in ServiceFormCustomerInputKey]: Yup.BaseSchema;
};

type ValidationCustomSpec = {
  [Property in CustomFormInputKey]: Yup.BaseSchema;
};

type CustomRadioInterface = {
  value: number;
  otherLabel: string;
};

const defaultValidationSpec = (
  customFieldValidations: ValidationCustomSpec,
): ValidationSpec => ({
  lastName: Yup.string().max(255).label("氏名（姓）"),
  firstName: Yup.string().max(255).label("氏名（名）"),
  lastNameKana: Yup.string().katakana().max(255).label("フリガナ（セイ）"),
  firstNameKana: Yup.string().katakana().max(255).label("フリガナ（メイ）"),
  email: Yup.string().trim().email().max(255).label("メールアドレス"),
  mailMagazine: Yup.boolean().label("メルマガ配信"),
  subEmail: Yup.string().trim().email().max(255).label("サブメールアドレス"),
  tel: Yup.string().tel().label("電話番号"),
  zip1: Yup.string()
    .matches(/^[0-9]{7}$/, {
      message: ({ label }) => `${label}は7桁の数字で入力してください`,
      excludeEmptyString: true,
    })
    .label("郵便番号"),
  prefectureId: Yup.number().positive().label("都道府県"),
  address1: Yup.string().max(255).label("市区町村以降の住所"),
  address2: Yup.string().max(255).label("建物名（住所2）"),
  birthday: Yup.string().label("生年月日"),
  age: Yup.number().min(0).max(120).label("年齢"),
  gender: Yup.number().label("性別"),
  lineId: Yup.string().min(1).max(255).label("LINE ID"),
  facebookId: Yup.string().min(1).max(255).label("Facebook ID"),
  twitterId: Yup.string().min(1).max(255).label("Twitter ID"),
  instagramId: Yup.string().min(1).max(255).label("Instagram ID"),
  companyName: Yup.string().max(255).label("会社名"),
  industryId: Yup.mixed().label("業種ID"),
  industryOther: Yup.string().max(255).label("業種その他"),
  position: Yup.string().max(255).label("役職"),
  companyHp: Yup.string().url().max(255).label("会社ホームページ"),
  department: Yup.string().max(255).label("部署名"),
  questionnaire: Yup.string().max(255).label("アンケート"),
  ...customFieldValidations,
});

const getCustomValidateInstance = (
  type: ServiceFormsType,
  name: string,
  isRequired: boolean,
  extraRule?: {
    type?: number;
    length?: number;
  },
) => {
  const guardMaxlength = (value: CustomRadioInterface[] | undefined) => {
    let checkFlag = true;
    if (value && value.length > 0) {
      value.forEach((item) => {
        if (item.value === 99 && item.otherLabel?.length > 255) {
          checkFlag = false;
        }
      });
    }
    return checkFlag;
  };
  const guardRequiredLabel = (value: CustomRadioInterface[] | undefined) => {
    let checkFlag = true;
    if (value && value.length > 0) {
      value.forEach((item) => {
        if (item.value === 99 && !item.otherLabel?.length) {
          checkFlag = false;
        }
      });
    }
    return checkFlag;
  };
  if (type === ServiceFormsType.OPTIONS) {
    if (
      extraRule &&
      extraRule.type === ServiceCustomFormsType.CHECKBOX_ALL &&
      extraRule.length
    ) {
      return isRequired
        ? Yup.array()
            .label(name)
            .max(255)
            .length(extraRule.length, "全てのチェック（同意）が必要です")
        : Yup.array()
            .label(name)
            .max(255)
            .optional()
            .test("maxlength", `全てのチェック（同意）が必要です。`, (value) =>
              guardMaxlength(value),
            );
    }
    return isRequired
      ? Yup.array()
          .label(name)
          .max(255)
          .min(1)
          .required()
          .test("maxlength", `${name} は255文字以内で入力して下さい`, (value) =>
            guardMaxlength(value),
          )
          .test("labelRequied", `${name} は必須入力です`, (value) =>
            guardRequiredLabel(value),
          )
      : Yup.array()
          .label(name)
          .max(255)
          .optional()
          .test("maxlength", `${name} は255文字以内で入力して下さい`, (value) =>
            guardMaxlength(value),
          );
  }
  if (type === ServiceFormsType.MULTI_LINE_DESCRIPTION) {
    return isRequired
      ? Yup.string().label(name).max(1000).min(1).required()
      : Yup.string().label(name).max(1000).optional();
  }
  if (type === ServiceFormsType.UPLOAD) {
    const requiredMessage = `${name}は必須入力です`;

    return isRequired
      ? Yup.array().label(name).max(255).min(1, requiredMessage).required()
      : Yup.array().label(name).max(255).optional();
  }

  return isRequired
    ? Yup.string().label(name).max(255).required("日前は必須入力です。")
    : Yup.string().label(name).max(255).optional();
};

const getErrors = (
  inputs: ServiceFormInputs,
  formData: ServiceFormData,
): ServiceFormErrors => {
  return {
    service: getServiceErrors(inputs, formData),
    customer: getCustomerErrors(inputs, formData),
  } as ServiceFormErrors;
};

const getCustomerErrors = (
  inputs: ServiceFormInputs,
  formData: ServiceFormData,
): ServiceFormErrors["customer"] => {
  let customFieldValidations: ValidationCustomSpec = {};
  const customFields = formData.customer.filter((item) =>
    checkIsCustomField(item.type),
  );

  customFields.forEach((item) => {
    const customInputKeys =
      item.type === ServiceFormsType.UPLOAD
        ? "files"
        : getCustomFieldKey(
            item.type,
            item.serviceFormId,
            item.customForm.serviceCustomFormId,
          );
    customFieldValidations = {
      ...customFieldValidations,
      [customInputKeys]: getCustomValidateInstance(
        item.type,
        item.customForm.label,
        item.customForm.isRequired,
        { type: item.customForm.type, length: item.customForm.options?.length },
      ),
    };
  });

  const spec = defaultValidationSpec(customFieldValidations);

  formData.customer.forEach((formCustomerData) => {
    const inputKeys = getCustomerInputKeys(formCustomerData.type);
    let customInputKeyValue = "";

    if (checkIsCustomField(formCustomerData.type)) {
      const customInputKeys =
        formCustomerData.type === ServiceFormsType.UPLOAD
          ? "files"
          : getCustomFieldKey(
              formCustomerData.type,
              formCustomerData.serviceFormId,
              formCustomerData.customForm.serviceCustomFormId,
            );
      customInputKeyValue = customInputKeys;
    }

    const keys = customInputKeyValue
      ? [...inputKeys, customInputKeyValue]
      : inputKeys;
    if (
      formCustomerData.customForm.isRequired &&
      formCustomerData.customForm.type !== ServiceCustomFormsType.INPUT_UPLOAD
    ) {
      for (const inputKey of keys) {
        if (inputKey === "industryOther") {
          spec[inputKey] = spec[inputKey].when("industryId", {
            is: 99,
            then: Yup.string().required(),
          });
        } else {
          spec[inputKey] = spec[inputKey].required() as Yup.BaseSchema;
          if (inputKey === "industryId") {
            spec[inputKey] = spec[inputKey].notOneOf(
              [""],
              `業種ID は必須入力です`,
            );
          }
        }
      }
    } else {
      for (const inputKey of keys) {
        spec[inputKey] = (
          spec[inputKey].default(null) as Yup.BaseSchema
        ).nullable() as Yup.BaseSchema;
      }
    }
  });

  const schema = Yup.object(spec);
  try {
    schema.validateSync(inputs.customer, { abortEarly: false });
  } catch (e) {
    if (e instanceof ValidationError) {
      const customerErrors = {} as ServiceFormErrors["customer"];
      e.inner.forEach((validationError) => {
        if (validationError.path) {
          customerErrors[validationError.path as ServiceFormCustomerInputKey] =
            validationError.errors;
        }
      });

      return customerErrors;
    } else {
      return {} as ServiceFormErrors["customer"];
    }
  }
  return {} as ServiceFormErrors["customer"];
};

const getServiceErrors = (
  inputs: ServiceFormInputs,
  formData: ServiceFormData,
): ServiceFormErrors["service"] => {
  if (formData?.service?.type === "calendar") {
    if (inputs.service?.serviceMenuId) {
      return {};
    }
    return {
      detailIds: ["メニューの選択が必要です"],
    };
  }
  if (formData?.service?.type === "subscription") {
    if (inputs.service?.detailIds.length > 0) {
      return {};
    }
    return {
      detailIds: ["メニューの選択が必要です"],
    };
  }

  if (
    !(
      formData?.service?.type === "event-seminar" ||
      formData?.service?.type === "form"
    )
  ) {
    return {};
  }
  if (formData?.service?.type === "event-seminar") {
    const { reserveStatus, capacity_status } = formData.service;
    const total = inputs.service.detailIds.reduce((sum, id) => {
      return sum + (inputs.service.quantity?.[id] || 0);
    }, 0);

    if (
      reserveStatus === ServiceReserveStatus.ALL &&
      capacity_status === CapacityStatus.ALL &&
      typeof formData.service.remaining_capacity === "number" &&
      total > formData.service.remaining_capacity
    ) {
      return {
        isErrorCapacity: true,
        isShowError: reserveStatus === ServiceReserveStatus.ALL,
      };
    }

    switch (reserveStatus) {
      case ServiceReserveStatus.ALL:
        return {};
      case ServiceReserveStatus.SINGLE_SELECT:
      case ServiceReserveStatus.MULTIPLE_SELECT:
        if (inputs.service.detailIds.length > 0) {
          return {};
        }
        return {
          detailIds: ["メニューの選択が必要です"],
        };
      default:
        assertNever(reserveStatus);
    }
  }
  return {};
};

interface SubmitResult {
  orderUuid: string;
  thanksUuid: string;
  id: number;
}

export const useHandleSubmitForm = () => {
  const putReservationOrderRequest = usePutReservationOrderRequest();
  const serviceFormUiState = useServiceFormUiState();

  return useCallback(
    async (formUuid?: string): Promise<SubmitResult> => {
      if (serviceFormUiState.status !== "Loaded") {
        throw new Error("サービスフォームが読み込まれていません。");
      }

      if (putReservationOrderRequest === undefined) {
        throw new Error("入力項目が足りません。");
      }

      let res;
      let customerEmail = "";

      if (serviceFormUiState.formData.service.type === "calendar") {
        res = await reservationApi.putReservationCalendarOrder(
          putReservationOrderRequest as ReservationApiPutReservationCalendarOrderRequest,
        );
        const calendarRequest =
          putReservationOrderRequest as ReservationApiPutReservationCalendarOrderRequest;
        const customer =
          calendarRequest.putReservationCalendarOrderRequest?.customer;
        customerEmail = customer?.email || "";
        localStorage.setItem(
          "customer_form",
          JSON.stringify({
            firstName: customer ? customer.first_name : "",
            lastName: customer ? customer.last_name : "",
            email: customerEmail,
          }),
        );
      } else {
        res = await reservationApi.putReservationOrder(
          putReservationOrderRequest as ReservationApiPutReservationOrderRequest,
        );
        const requestData =
          putReservationOrderRequest as ReservationApiPutReservationOrderRequest;
        const customer = requestData.putReservationOrderRequest?.customer;
        customerEmail = customer?.email || "";
        localStorage.setItem(
          "customer_form",
          JSON.stringify({
            firstName: customer ? customer.first_name : "",
            lastName: customer ? customer.last_name : "",
            email: customerEmail,
          }),
        );
      }

      // Post content access log after successful order submission
      // This ensures postlog is ALWAYS called regardless of where order is submitted

      if (formUuid) {
        try {
          // Get CSRF token first to ensure postLog succeeds
          await contentAccessLogApi.postLog({
            pageUuid: formUuid,
            orderId: res.data.id,
            email: customerEmail || undefined,
          });
        } catch {
          // Log error for debugging but don't break the order flow
        }
      }

      return {
        orderUuid: res.data.order_uuid,
        thanksUuid: res.data.thanks_uuid || "",
        id: res.data.id,
      };
    },
    [serviceFormUiState, putReservationOrderRequest],
  );
};

export const useSetFormPayment = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    (payment: FormPayment) => {
      setServiceForm((prev) => ({
        ...prev,
        payment: {
          ...prev.payment,
          ...payment,
        },
      }));
    },
    [setServiceForm],
  );
};

export const useSetFormPaymentMethod = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    (paymentMethod: CardElementFormPayment) => {
      setServiceForm((prev) => ({
        ...prev,
        paymentMethod: paymentMethod,
      }));
    },
    [setServiceForm],
  );
};

export const useSetFormClientSecret = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    (clientSecret: string) => {
      setServiceForm((prev) => ({
        ...prev,
        clientSecret: clientSecret,
      }));
    },
    [setServiceForm],
  );
};

export const useResetFormClientSecret = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(() => {
    setServiceForm((prev) => ({
      ...prev,
      clientSecret: undefined,
    }));
  }, [setServiceForm]);
};

export const useClearStalePaymentSelection = () => {
  const setFormPaymentMethod = useSetFormPaymentMethod();
  const resetFormClientSecret = useResetFormClientSecret();

  return useCallback(
    (billingAmount: number) => {
      if (billingAmount > 0) {
        setFormPaymentMethod({});
        resetFormClientSecret();
      }
    },
    [resetFormClientSecret, setFormPaymentMethod],
  );
};

export const useSetIsSummited = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    (formData: ServiceFormData) => {
      setServiceForm((prev) => {
        return {
          ...prev,
          formData,
        };
      });
    },
    [setServiceForm],
  );
};

export const useSetIsAgreePolicy = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);

  return useCallback(
    (isAgreePolicy: boolean) => {
      setServiceForm((prev) => {
        return {
          ...prev,
          isAgreePolicy: isAgreePolicy,
        };
      });
    },
    [setServiceForm],
  );
};

// Hook to validate reservation via API
export const useValidateReservation = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);
  const setLoading = useSetAtom(loadingAtom);
  const serviceFormState = useAtomValue(serviceFormAtom);

  return useCallback(async () => {
    const { inputs, formData } = serviceFormState;

    // Only validate for event-seminar service
    if (formData.service.type !== "event-seminar") {
      return { isValid: true, error: null };
    }

    // Only validate when detailIds are selected
    if (!inputs.service.detailIds || inputs.service.detailIds.length === 0) {
      // Clear validation error when no detailIds
      setServiceForm((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          service: {
            ...prev.errors.service,
            validationError: undefined,
          },
        },
      }));
      return { isValid: true, error: null };
    }

    // Start loading
    const loadingTaskId = new Date().getTime().toString();
    setLoading((prev) => ({
      tasks: [...prev.tasks, { id: loadingTaskId }],
    }));

    try {
      // Prepare request body according to required format
      const requestBody = {
        service: {
          id: formData.service.id,
          service_details: inputs.service.detailIds.map((detailId) => ({
            id: detailId,
            quantity: inputs.service.quantity?.[detailId] || 1,
          })),
        },
      };

      // Call validate API
      await reservationApi.invalidateReservation({
        invalidateReservationRequest: requestBody,
      });

      // If no error, clear validation error
      setServiceForm((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          service: {
            ...prev.errors.service,
            validationError: undefined,
          },
        },
      }));

      return { isValid: true, error: null };
    } catch (error) {
      // If API returns error, set validation error
      setServiceForm((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          service: {
            ...prev.errors.service,
            validationError:
              "定員の残数に対して、チケット数が上限を超えました。お手数ですがチケット数を変更して申込みしてください。",
          },
        },
      }));

      return {
        isValid: false,
        error:
          "定員の残数に対して、チケット数が上限を超えました。お手数ですがチケット数を変更して申込みしてください。",
      };
    } finally {
      // End loading
      setLoading((prev) => ({
        tasks: prev.tasks.filter((task) => task.id !== loadingTaskId),
      }));
    }
  }, [serviceFormState, setServiceForm, setLoading]);
};
